import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../lib/usePageMeta';
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { defaultStay, stayQuery, useStayParams } from '../lib/stayParams';
import { useSettings } from '../lib/useSettings';
import { RoomCard } from '../components/RoomCard';
import { SingleRoom } from '../components/SingleRoom';
import { Notice, Section } from '../components/Section';
import { StayPicker } from '../components/StayPicker';
import { clockTime, shortDate } from '../lib/format';
import { SEO } from '../seo/pages';

export function RoomsPage() {
  usePageMeta(SEO.rooms.title, SEO.rooms.description, { jsonLd: SEO.rooms.jsonLd });
  const settingsQuery = useSettings();
  const settings = settingsQuery.data;
  const { stay, update, hasDates } = useStayParams();

  // First visit: preselect the event's first nights so the picker is not empty.
  useEffect(() => {
    if (!hasDates && settings) {
      const preset = defaultStay(settings);
      if (preset) update(preset);
    }
  }, [hasDates, settings, update]);

  const window = hasDates ? { checkIn: stay.checkIn!, checkOut: stay.checkOut! } : null;
  // Wait for the preset above before asking for the catalog, so a first visit
  // costs one request instead of an aborted one plus a retry. If settings
  // fail or carry no window, fetch without dates.
  const datesSettled =
    hasDates ||
    settingsQuery.isError ||
    (settingsQuery.isSuccess && defaultStay(settingsQuery.data) === null);
  const rooms = useQuery({
    queryKey: ['rooms', window?.checkIn, window?.checkOut],
    queryFn: ({ signal }) => api.rooms.list(window, signal),
    enabled: datesSettled,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  const roomsError =
    rooms.error instanceof ApiClientError && rooms.error.code === 'rate_limited'
      ? 'The site is busy right now. Give it a moment and try again.'
      : 'We could not load rooms right now. Please try again.';

  const query = stayQuery(stay);
  const bookingClosed = settings && !settings.bookingOpen;
  const single = rooms.data && rooms.data.rooms.length === 1 ? rooms.data.rooms[0]! : null;
  // With one fixed night and one room the picker has nothing to pick; the
  // feature block states the night itself.
  const showPicker = !single;

  return (
    <Section eyebrow="Reserve your room" title="Rooms at the hotel">
      {settings?.hotelName && (
        <p className="-mt-4 mb-8 max-w-3xl text-ink-400">
          {settings.hotelName}
          {settings.hotelAddress ? `, ${settings.hotelAddress}` : ''}. Check-in from{' '}
          {clockTime(settings.checkInTime)}, check-out by {clockTime(settings.checkOutTime)}.
          {settings.bookableCheckInMin && settings.bookableCheckOutMax
            ? ` Rooms are for the night of ${shortDate(settings.bookableCheckInMin)}, checking out ${shortDate(settings.bookableCheckOutMax)}.`
            : ''}{' '}
          A room is your RSVP to the celebration. Not staying over?{' '}
          <Link to="/rsvp" className="text-pink-300 underline hover:text-pink-200">
            RSVP here
          </Link>
          .
        </p>
      )}

      {bookingClosed && (
        <div className="mb-6">
          <Notice tone="warning">Room reservations are not open right now. Check back soon.</Notice>
        </div>
      )}

      {showPicker && (
        <div className="mb-8  border border-night-700 bg-night-900 p-5">
          <StayPicker stay={stay} settings={settings} onChange={update} />
        </div>
      )}

      {rooms.data?.allSoldOut && (
        <div className="mb-6">
          <Notice tone="danger">
            {settings?.soldOutMessage ??
              'Every room for these dates is spoken for. Try different dates, or support the family another way.'}
          </Notice>
        </div>
      )}

      {rooms.isPending && !rooms.error && (
        <div
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          aria-busy="true"
          aria-label="Checking availability"
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[4/3] bg-night-800" />
              <div className="mt-4 h-5 w-2/3 bg-night-800" />
              <div className="mt-3 h-4 w-1/2 bg-night-800" />
            </div>
          ))}
        </div>
      )}
      {rooms.error && <Notice tone="danger">{roomsError}</Notice>}

      {rooms.data && rooms.data.rooms.length === 0 && (
        <p className="text-ink-400">No rooms have been published yet.</p>
      )}

      {single && <SingleRoom room={single} settings={settings} query={query} />}

      {rooms.data && rooms.data.rooms.length > 1 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {rooms.data.rooms.map((room) => (
            <RoomCard key={room.id} room={room} query={query} />
          ))}
        </div>
      )}
    </Section>
  );
}
